/**
 * The `useChat` `onToolCall` handler.
 *
 * Routes a model-forwarded tool call. The read tool auto-runs and its output is
 * supplied via `addToolOutput` so the loop resumes (paired with
 * `sendAutomaticallyWhen`). A mutating tool is routed through its proposal gate
 * (`plan`): a recoverable outcome is fed straight back so the model re-asks;
 * a proposal is registered for the card and produces NO tool output until
 * the clinician explicitly Applies/Rejects (structural HITL). A tool with no
 * registered gate stays fail-closed (never auto-fulfilled).
 */
import {
    runToolCall,
    type ToolCallLike,
    type ToolRunnerDeps,
} from './toolRunner';
import type {
    AnalyzeImageForModelSelectionOutput,
    ClearContoursOutcome,
    ListedStructure,
    CheckPacsConnectionOutput,
    ListImportedPatientsOutput,
    ListPatientStudiesOutput,
    LoadSeriesOutcome,
    ModelActionOutcome,
    SearchPatientsOutput,
    ZoomViewOutput,
    CheckAutoSegmentationJobsOutput,
    JobActionOutcome,
    SendStructureSetOutcome,
    SetStructureVisibilityOutput,
    GoToPositionOutput,
    SetWindowLevelOutput,
    SetStructureColorOutcome,
    StructureChangeOutcome,
    GetStructurePropertiesOutput,
    ResetDisplayOutput,
    ShowInViewerOutput,
    ListTimepointsOutput,
    CompareStructuresOutput,
} from '../../contract';
import type { MutatingPlan, MutationOutcome } from './mutatingToolRunner';

/**
 * A read tool that failed: reported to the model as a tool error so the turn
 * completes (an unanswered call would block the next message) and the model
 * can tell the clinician what went wrong, instead of a fabricated result.
 */
export interface AddToolErrorArgs {
    readonly tool: string;
    readonly toolCallId: string;
    readonly state: 'output-error';
    readonly errorText: string;
}

export type AddToolOutputArgs = AddToolResultArgs | AddToolErrorArgs;

export interface AddToolResultArgs {
    readonly tool: string;
    readonly toolCallId: string;
    readonly output:
        | ReadonlyArray<ListedStructure>
        | MutationOutcome
        | ModelActionOutcome
        | AnalyzeImageForModelSelectionOutput
        | SearchPatientsOutput
        | ListPatientStudiesOutput
        | ListImportedPatientsOutput
        | CheckPacsConnectionOutput
        | LoadSeriesOutcome
        | ClearContoursOutcome
        | ZoomViewOutput
        | CheckAutoSegmentationJobsOutput
        | JobActionOutcome
        | SendStructureSetOutcome
        | SetStructureVisibilityOutput
        | GoToPositionOutput
        | SetWindowLevelOutput
        | SetStructureColorOutcome
        | StructureChangeOutcome
        | GetStructurePropertiesOutput
        | ResetDisplayOutput
        | ShowInViewerOutput
        | ListTimepointsOutput
        | CompareStructuresOutput;
}

/** An additional read-only tool, auto-run with no human gate. */
export type ReadOpHandler = (
    call: ToolCallLike
) => Promise<
    | AnalyzeImageForModelSelectionOutput
    | SearchPatientsOutput
    | ListPatientStudiesOutput
    | ListImportedPatientsOutput
    | CheckPacsConnectionOutput
    | ZoomViewOutput
    | CheckAutoSegmentationJobsOutput
    | SetStructureVisibilityOutput
    | GoToPositionOutput
    | SetWindowLevelOutput
    | GetStructurePropertiesOutput
    | ResetDisplayOutput
    | ShowInViewerOutput
    | ListTimepointsOutput
    | CompareStructuresOutput
>;

/** A recoverable plan outcome the model re-asks against — carries no proposal. */
export interface RecoverableOutcome {
    readonly toolCallId: string;
    readonly output: MutationOutcome;
}

/**
 * One mutating tool's gate as a single self-contained step: plan the call and,
 * on a proposal, register it for the card; on a recoverable outcome, hand it to
 * `onRecoverable` (the model re-asks). The proposal type stays encapsulated
 * inside the handler, so the four ops share one dispatch map with no cast —
 * build entries with {@link mutatingOpHandler}.
 */
export interface MutatingOpHandler {
    run(
        call: ToolCallLike,
        onRecoverable: (recoverable: RecoverableOutcome) => void
    ): Promise<void>;
}

/**
 * Tie a typed plan to its register for the uniform dispatch map. `P` never
 * leaves this closure — the plan↔register pairing is checked here at the wiring
 * site, and no cast is needed at dispatch.
 */
export function mutatingOpHandler<P>(
    plan: (call: ToolCallLike) => Promise<MutatingPlan<P>>,
    register: (proposal: P) => void
): MutatingOpHandler {
    return {
        async run(call, onRecoverable) {
            const result = await plan(call);
            if (result.kind === 'recoverable') {
                onRecoverable({
                    toolCallId: result.toolCallId,
                    output: result.output,
                });
            } else {
                // No tool output: the loop stays halted at the HITL gate until
                // the clinician Applies/Rejects on the card.
                register(result.proposal);
            }
        },
    };
}

export interface HandleToolCallArgs extends ToolRunnerDeps {
    readonly toolCall: ToolCallLike;
    readonly addToolOutput: (args: AddToolOutputArgs) => void;
    /** Mutating tools keyed by name. A tool absent here stays fail-closed. */
    readonly mutatingOps?: Record<string, MutatingOpHandler>;
    /** Extra read-only tools keyed by name (auto-run, like list_structures). */
    readonly readOps?: Record<string, ReadOpHandler>;
}

export async function handleAgentToolCall({
    toolCall,
    addToolOutput,
    structures,
    mutatingOps,
    readOps,
}: HandleToolCallArgs): Promise<void> {
    const op = mutatingOps?.[toolCall.toolName];
    if (op) {
        await op.run(toolCall, ({ toolCallId, output }) =>
            addToolOutput({ tool: toolCall.toolName, toolCallId, output })
        );
        return;
    }

    const read = readOps?.[toolCall.toolName];
    if (read) {
        let output: Awaited<ReturnType<ReadOpHandler>>;
        try {
            output = await read(toolCall);
        } catch (err) {
            addToolOutput({
                tool: toolCall.toolName,
                toolCallId: toolCall.toolCallId,
                state: 'output-error',
                errorText:
                    err instanceof Error && err.message
                        ? err.message
                        : 'the viewer could not complete this read',
            });
            return;
        }
        addToolOutput({
            tool: toolCall.toolName,
            toolCallId: toolCall.toolCallId,
            output,
        });
        return;
    }

    const outcome = await runToolCall(toolCall, { structures });
    if (outcome.kind === 'auto-output') {
        addToolOutput({
            tool: outcome.toolName,
            toolCallId: outcome.toolCallId,
            output: outcome.output,
        });
    }
    // 'requires-gate': intentionally no output (fail-closed).
}
